/**
 * Conversão TemplateLibraryItem → ContentBlock[].
 *
 * Espelha o shape do FB Messenger no formato que o Broadcast e o Flow Builder
 * consomem (ContentBlock unificado). Centraliza a heurística pra não duplicar
 * no Broadcast e no Flow.
 *
 * Regras de mapeamento:
 *   - generic (card) com imagem: 1 bloco IMAGE com title/subtitle/buttons
 *   - generic (card) sem imagem: 1 bloco TEXT com message="title\n\nsubtitle" + buttons
 *   - button (texto + botões): 1 bloco TEXT com message=text_content + buttons
 *
 * Cada button do template (buttons_json[*].url/title) vira um BlockButton type='url'
 * com url_entries[{url, weight:100}] — formato suportado pelo BlocksEditor.
 */

import type { ContentBlock, BlockButton } from '@/types/flow';
import type { TemplateLibraryItem } from '@/types/template-library';

function mapButtons(template: TemplateLibraryItem): BlockButton[] {
    const list = template.buttons_json ?? [];
    return list
        .filter((b) => b && (b.url || b.title))
        .map<BlockButton>((b) => ({
            text: (b.title || '').trim() || 'Botão',
            type: 'url',
            value: '',
            url_entries: b.url
                ? [{ url: b.url, weight: 100 }]
                : undefined,
        }));
}

export function templateToBlocks(template: TemplateLibraryItem): ContentBlock[] {
    const buttons = mapButtons(template);
    const isCard = template.template_type === 'generic';
    const hasImage = !!template.image_url;

    if (isCard && hasImage) {
        const block: ContentBlock = {
            type: 'image',
            media_url: template.image_url || undefined,
            title: template.title || undefined,
            subtitle: template.subtitle || undefined,
            image_aspect_ratio: 'horizontal',
            buttons: buttons.length > 0 ? buttons : undefined,
        };
        return [block];
    }

    // Card sem imagem ou button template → bloco de texto
    let message: string;
    if (isCard) {
        const parts = [template.title, template.subtitle].filter(Boolean) as string[];
        message = parts.join('\n\n');
    } else {
        message = template.text_content || template.title || '';
    }

    const block: ContentBlock = {
        type: 'text',
        message: message.trim() || '(sem texto)',
        buttons: buttons.length > 0 ? buttons : undefined,
    };
    return [block];
}
